import Icon from '@components/icon';
import cn from 'clsx';
import { observer } from 'mobx-react-lite';
import { useMemo } from 'react';

import { ActionPopover } from 'components/ActionPopover';
import { FollowedLoadsPagination } from 'components/FollowedLoadsPagination';
import { FollowedLoadsPerPageSelect } from 'components/FollowedLoadsPerPageSelect';
import { useStore } from 'hooks/useStore';
import { PrettyLoad } from 'layout/Search/PrettyLoad';
import { TabsContent } from 'layout/TabsContent';
import { followedLoadsTabIds } from 'store/UIStore/UIStore';

import css from './ContentWithFavorites.module.css';

const ContentWithFavorites = () => {
  const {
    app: { i18n },
    view,
    followedLoads: { favorites, loadsWithOffers, loadsWithAuctions },
    etrnPromo,
    ui: { pagination, followedLoadsTab, handleTabChange },
  } = useStore();

  const callbacks = useMemo(
    () => ({
      hideLoads: () => {},
      selectLoads: favorites.selectedLoads.toggleSelection,
      onRemoveLoadFromFavorites: favorites.updateLoads,
      onOfferChange: loadsWithOffers.updateLoads,
      onCounterOfferSend: etrnPromo.showPopupAfterCounterOffer,
      onAuctionChange: loadsWithAuctions.updateLoads,
      onFilterTabOpen: handleTabChange,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  if (favorites.count === 0) {
    return (
      <TabsContent.NoContent
        className={css.noContent}
        desc={''}
        icon={<Icon color="var(--glz-color-neutral-tone-4)" name="ic_cargo-starred" size="xl" />}
        title={i18n.favorites.noContent.favorites.title}
      />
    );
  }

  return (
    <div>
      <div className={css.heading}>
        <h2 className={cn(css.totalItems, 'glz-h glz-is-size-2')}>{view.pluralizedCount(favorites.count, 'loads')}</h2>

        <FollowedLoadsPagination />
      </div>

      <div className={cn(css.actions, css.actions_top)}>
        <div className={css.links}>
          {followedLoadsTab.data === followedLoadsTabIds.onlyWithFavorites && (
            <>
              <ActionPopover
                alternateAction={i18n.actionPopover.delete.atlernate}
                primaryAction={i18n.actionPopover.delete.primary}
                title={i18n.favorites.deleteUnactual.popover}
                onConfirm={favorites.removeUnactualFavorites}
              >
                <button className={cn('glz-link', css.link)}>{i18n.favorites.deleteUnactual.button}</button>
              </ActionPopover>

              <ActionPopover
                alternateAction={i18n.actionPopover.delete.atlernate}
                primaryAction={i18n.actionPopover.delete.primary}
                title={i18n.favorites.deleteAll.popover}
                onConfirm={favorites.removeAllFavorites}
              >
                <button className={cn('glz-link', css.link)}>{i18n.favorites.deleteAll.button}</button>
              </ActionPopover>
            </>
          )}
        </div>

        <FollowedLoadsPerPageSelect isRow />
      </div>

      <PrettyLoad
        callbacks={callbacks}
        currentPage={pagination.pages.onlyWithFavorites}
        descriptor={{ isFavorites: true }}
        loads={favorites.loads}
        selectedLoadsIds={favorites.selectedLoads.plainIds}
        updateLoad={favorites.updateLoad}
      />

      {pagination.totalFavoritesPages > 1 && (
        <div className={cn(css.actions, css.actions_bottom)}>
          <FollowedLoadsPagination className={css.pagination} />

          <FollowedLoadsPerPageSelect containerClassName={css.sortField} />
        </div>
      )}
    </div>
  );
};

const connected = observer(ContentWithFavorites);

export { connected as ContentWithFavorites };
